// src/data/caseHistoryRename.js
//
// RULING 319 (Bob 2026-10-16): every case history in two copies - the original sealed as is, and a RENAMED copy for learning.
// This module is the renaming engine for the second copy. The rules it carries were agreed with Bob on 2026-10-16:
//   - people by role code, one code per person for the whole case (CO1, TM1, TS1, ATS1, HO1, OW1, CL1, CE1, LC1, GC1 ...);
//   - work locations Yard A, Yard B ...; train symbols, jobs, case/claim/file/award numbers - fictional, consistent substitutes;
//   - dates as day counts from the case's first record - "Day 12 (Tuesday)", federal holidays named;
//   - removed, never faked: SSNs, employee and membership numbers, phones, e-mails, signatures, and health matters;
//   - untouched: entities, agreements and rules, quoted agreement language, intervals, amounts, what was said and done.
// Automated renaming is CHECKED, NOT TRUSTED (319 item 5): renameText() returns what it could not settle - capitalised word
// pairs that may be names, long numbers that may be identifiers, dates it could not read - for a person to look at.
//
// PURE. No Firestore, no clock. Shared with the server (scripts\sync-shared-modules.js).

export const BANNER = 'RENAMED COPY FOR LEARNING. Names, places and numbers in this copy are substitutes. The events, the day '
  + 'counts and the quoted agreement language are real. Union teaching material - not for publication.';

// role -> code prefix. Job titles take precedence over the side's generic code when the title is known.
export const ROLE_PREFIX = Object.freeze({
  trainmaster: 'TM', terminal_superintendent: 'TS', assistant_terminal_superintendent: 'ATS', hearing_officer: 'HO',
  road_foreman: 'RF', general_yardmaster: 'GYM', superintendent: 'SUPT', labor_relations: 'LR',
  carrier_official: 'CO', carrier_witness: 'CO',
  organization_witness: 'OW', claimant: 'CL', charged_employee: 'CE', local_chair: 'LC', general_chair: 'GC',
  organization_officer: 'OO', member: 'M', other: 'P',
});

const s = (v) => (v == null ? '' : String(v)).trim();
const reEsc = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const letterCode = (i) => (i < 26 ? LETTERS[i] : LETTERS[Math.floor(i / 26) - 1] + LETTERS[i % 26]);

// Surnames that are also ordinary words: replaced only in a full-name or titled form, never alone.
const COMMON_WORD_SURNAMES = new Set(['young', 'price', 'rule', 'mark', 'king', 'hill', 'wood', 'love', 'may', 'march', 'bell',
  'long', 'short', 'little', 'white', 'black', 'brown', 'green', 'gray', 'grey', 'rice', 'cook', 'baker', 'mason', 'carter',
  'walker', 'turner', 'miller', 'parker', 'hunter', 'fisher', 'porter', 'chase', 'hope', 'case', 'page', 'lane', 'banks',
  'field', 'ford', 'cross', 'french', 'english', 'west', 'north', 'south', 'east', 'day', 'law', 'best', 'hall', 'rose']);

// The name forms a person may appear under in a record.
export function nameVariants(fullName, aliases) {
  const out = new Set();
  const add = (v) => { v = s(v).replace(/\s+/g, ' '); if (v.length > 1) out.add(v); };
  const n = s(fullName).replace(/\s+/g, ' ');
  if (!n) return [];
  add(n);
  const parts = n.replace(/,/g, '').split(' ').filter(Boolean);
  const suffix = /^(jr|sr|ii|iii|iv)\.?$/i;
  const core = parts.filter((p) => !suffix.test(p));
  if (core.length >= 2) {
    const first = core[0], last = core[core.length - 1];
    add(first + ' ' + last);
    add(first[0] + '. ' + last); add(first[0] + '.' + last); add(first[0] + ' ' + last);
    if (core.length >= 3) add(first + ' ' + core[1][0] + '. ' + last);
    ['Mr.', 'Mr', 'Mrs.', 'Ms.', 'Ms', 'Miss'].forEach((t) => add(t + ' ' + last));
    if (!COMMON_WORD_SURNAMES.has(last.toLowerCase()) && last.length > 2) add(last);
    add(last + ', ' + first);
  }
  (Array.isArray(aliases) ? aliases : []).forEach(add);
  return [...out].sort((a, b) => b.length - a.length);
}

// participants: [{ name, role, aliases? }]; places: [names]; trains, jobs, numbers: [strings].
export function buildCodebook({ people, places, trains, jobs, numbers } = {}) {
  const counters = {};
  const book = { people: [], places: [], trains: [], jobs: [], numbers: [], problems: [] };
  (Array.isArray(people) ? people : []).forEach((p) => {
    const name = s(p && p.name);
    if (!name) return;
    if (book.people.some((x) => x.name.toLowerCase() === name.toLowerCase())) return;   // one code per person
    const role = ROLE_PREFIX[p.role] ? p.role : 'other';
    if (!ROLE_PREFIX[p.role]) book.problems.push('No known role for "' + name + '" - coded as P (person); give the role.');
    const pre = ROLE_PREFIX[role];
    counters[pre] = (counters[pre] || 0) + 1;
    book.people.push({ name, role, code: pre + counters[pre], variants: nameVariants(name, p.aliases) });
  });
  const seq = (list, label, key) => (Array.isArray(list) ? list : []).map(s).filter(Boolean)
    .filter((v, i, a) => a.findIndex((x) => x.toLowerCase() === v.toLowerCase()) === i)
    .forEach((v, i) => book[key].push({ name: v, code: label(i) }));
  seq(places, (i) => 'Yard ' + letterCode(i), 'places');
  seq(trains, (i) => 'Train T' + (i + 1), 'trains');
  seq(jobs, (i) => 'Job J' + (i + 1), 'jobs');
  seq(numbers, (i) => 'File No. ' + (100 + i + 1), 'numbers');
  return book;
}

// ---- dates -> day counts ---------------------------------------------------------------------------------------------
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
const utc = (y, m, d) => Date.UTC(y, m, d);
function nthWeekday(y, m, wd, n) { const first = new Date(utc(y, m, 1)).getUTCDay(); return 1 + ((wd - first + 7) % 7) + (n - 1) * 7; }
function lastWeekday(y, m, wd) { const last = new Date(utc(y, m + 1, 0)); return last.getUTCDate() - ((last.getUTCDay() - wd + 7) % 7); }
// U.S. federal holidays on their observed dates (Saturday -> Friday, Sunday -> Monday). An agreement's own holiday list may
// differ; the copy says "federal holiday" and nothing more.
export function federalHoliday(iso) {
  const [y, m, d] = iso.split('-').map(Number); const mo = m - 1;
  const fixed = [[0, 1, "New Year's Day"], [6, 4, 'Independence Day'], [10, 11, 'Veterans Day'], [11, 25, 'Christmas Day']];
  if (y >= 2021) fixed.push([5, 19, 'Juneteenth']);
  for (const [fm, fd, name] of fixed) {
    let t = new Date(utc(y, fm, fd)); const wd = t.getUTCDay();
    if (wd === 6) t = new Date(utc(y, fm, fd - 1)); else if (wd === 0) t = new Date(utc(y, fm, fd + 1));
    if (t.getUTCMonth() === mo && t.getUTCDate() === d) return name + (wd === 6 || wd === 0 ? ' (observed)' : '');
  }
  if (mo === 11 && d === 31 && new Date(utc(y + 1, 0, 1)).getUTCDay() === 6) return "New Year's Day (observed)";
  const floating = [[0, 1, 3, 'Martin Luther King Jr. Day'], [1, 1, 3, "Washington's Birthday"], [8, 1, 1, 'Labor Day'],
    [9, 1, 2, 'Columbus Day'], [10, 4, 4, 'Thanksgiving Day']];
  for (const [fm, wd, n, name] of floating) if (mo === fm && d === nthWeekday(y, fm, wd, n)) return name;
  if (mo === 4 && d === lastWeekday(y, 4, 1)) return 'Memorial Day';
  return null;
}
function isoOf(y, m, d) {
  if (y < 100) y += y < 50 ? 2000 : 1900;
  const t = new Date(utc(y, m - 1, d));
  if (t.getUTCFullYear() !== y || t.getUTCMonth() !== m - 1 || t.getUTCDate() !== d) return null;
  return t.toISOString().slice(0, 10);
}
export function dayLabel(iso, day0) {
  const n = Math.round((Date.parse(iso + 'T00:00:00Z') - Date.parse(day0 + 'T00:00:00Z')) / 86400000);
  const wd = WEEKDAYS[new Date(iso + 'T00:00:00Z').getUTCDay()];
  const h = federalHoliday(iso);
  return 'Day ' + n + ' (' + wd + (h ? ', federal holiday: ' + h : '') + ')';
}
// Every date the text writes, with where it sits. Formats: 10/16/2026, 10/16/26, 2026-10-16, October 16, 2026, Oct. 16, 2026,
// 16 October 2026. A month and day with no year is NOT converted (it cannot be placed) - it is returned as unsettled.
const MON = '(Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|June?|July?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\\.?';
const DATE_RES = [
  { re: /\b(\d{4})-(\d{2})-(\d{2})\b/g, f: (m) => isoOf(+m[1], +m[2], +m[3]) },
  { re: /\b(\d{1,2})\/(\d{1,2})\/(\d{4}|\d{2})\b/g, f: (m) => isoOf(+m[3], +m[1], +m[2]) },
  { re: new RegExp('\\b' + MON + '\\s+(\\d{1,2})(?:st|nd|rd|th)?,?\\s+(\\d{4})\\b', 'gi'), f: (m) => isoOf(+m[3], monthNo(m[1]), +m[2]) },
  { re: new RegExp('\\b(\\d{1,2})\\s+' + MON + ',?\\s+(\\d{4})\\b', 'gi'), f: (m) => isoOf(+m[3], monthNo(m[2]), +m[1]) },
];
function monthNo(t) { const k = t.toLowerCase().replace('.', '').slice(0, 3); return MONTHS.findIndex((x) => x.startsWith(k)) + 1; }

// ---- the engine ----------------------------------------------------------------------------------------------------
const REMOVALS = [
  { re: /\b\d{3}-\d{2}-\d{4}\b/g, to: '[SSN removed]' },
  { re: /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, to: '[e-mail removed]' },
  { re: /(?:\+?1[\s.-]?)?\(?\b\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}\b/g, to: '[phone removed]' },
  { re: /\b(?:employee|emp\.?|ID|I\.D\.|badge|membership|member|payroll|file)\s*(?:no\.?|number|#)\s*:?\s*[A-Z0-9-]{3,}\b/gi, to: '[identifier removed]' },
  { re: /\b(?:date of birth|DOB|born on)\b[^.;\n]*/gi, to: '[birth date removed]' },
  { re: /\b(?:seniority date|roster (?:position|no\.?|number)|rank)\s*:?\s*[^.;\n]*/gi, to: '[seniority detail removed]' },
];
// A sentence carrying one of these is reduced to "a medical matter" - health, testing, leave and family matters (319).
const MEDICAL = /\b(diagnos\w*|surger\w*|surgical|prescri\w*|medication\w*|hospital\w*|physician\w*|doctor\w*|EAP|FMLA|pregnan\w*|urinalysis|breath alcohol|drug (?:test|screen)\w*|alcohol test\w*|tested positive|positive (?:test|result)|psychiatr\w*|counsel+ing for|rehab\w*)\b/i;
const SAFE_PAIRS = /^(Rule|Article|Section|Appendix|Day|Yard|Train|Job|File|Local|General|Chairperson|Chairman|Carrier|Organization|Board|Division|Award|Agreement|National|Railway|Railroad|Company|Transportation|Union|Labor|Act|United|States|Federal|Holiday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday|January|February|March|April|May|June|July|August|September|October|November|December|Hearing|Officer|Notice|Investigation|Claim|Discipline|Exhibit|Letter|Request|Decision|Appeal|Superintendent|Trainmaster|Yardmaster|Assistant|Terminal|Conrail|Amtrak|CSX|BNSF|Soo|Line|Metra|Delaware|Hudson|Canadian|Pacific|Norfolk|Southern|Union|Pacific|SMART|TD|UTU|NRAB|NMB|Public|Law|Special|Adjustment|First|Second|Third|Fourth|The|This|That|In|On|At|Of|For|And|By|To|A|An)$/;

function replaceAll(text, from, to) {
  if (!from) return text;
  const bounded = new RegExp('(^|[^A-Za-z0-9])(' + reEsc(from) + ")('s)?(?=$|[^A-Za-z0-9])", 'g');
  return text.replace(bounded, (m, pre, _w, poss) => pre + to + (poss || ''));
}

// text -> { text, unsettled: [{ kind, what }] }. day0 = the case's first record (YYYY-MM-DD).
export function renameText(input, book, day0) {
  let t = String(input == null ? '' : input);
  const unsettled = [];
  // signatures first: "/s/ Name", "s/ Name", "Signed: Name" -> "signed: CODE"
  // The name is at most four capitalised words or initials, and stops at a period that ends it - the first form of this
  // pattern took up to 60 characters and swallowed the next sentence (caught by test-case-history-rename, 2026-10-16).
  t = t.replace(/(?:^|\s)(?:\/s\/|s\/|signed:?)\s*([A-Z](?:[a-z'-]+|\.)(?:\s+[A-Z](?:[a-z'-]+|\.)){0,3})/g, (m, who) => {
    const p = (book.people || []).find((x) => x.variants.some((v) => who.trim().toLowerCase().startsWith(v.toLowerCase())));
    return ' [signed: ' + (p ? p.code : 'a signatory') + ']';
  });
  REMOVALS.forEach(({ re, to }) => { t = t.replace(re, to); });
  // dates -> day counts
  if (day0 && /^\d{4}-\d{2}-\d{2}$/.test(day0)) {
    DATE_RES.forEach(({ re, f }) => { t = t.replace(re, (...m) => { const iso = f(m); if (!iso) { unsettled.push({ kind: 'date', what: m[0] }); return m[0]; } return dayLabel(iso, day0); }); });
  } else unsettled.push({ kind: 'date', what: 'no first-record date given - dates left as written' });
  const lone = new RegExp('\\b' + MON + '\\s+\\d{1,2}(?:st|nd|rd|th)?\\b(?!,?\\s+\\d{4})', 'g');
  (t.match(lone) || []).forEach((w) => { if (!/^May\s/.test(w) || /^May\s+\d/.test(w)) unsettled.push({ kind: 'date without a year', what: w }); });
  // people (longest variant first, across everyone), then places, trains, jobs, numbers
  const swaps = [];
  (book.people || []).forEach((p) => p.variants.forEach((v) => swaps.push([v, p.code])));
  ['places', 'trains', 'jobs', 'numbers'].forEach((k) => (book[k] || []).forEach((x) => swaps.push([x.name, x.code])));
  swaps.sort((a, b) => b[0].length - a[0].length).forEach(([from, to]) => { t = replaceAll(t, from, to); });
  // health matters
  t = t.split(/(?<=[.!?])\s+/).map((sentence) => (MEDICAL.test(sentence) ? '[a medical matter - removed]' : sentence)).join(' ');
  // what the engine could not settle
  (t.match(/\b[A-Z][a-z]{2,}(?:\s+[A-Z]\.)?\s+[A-Z][a-z]{2,}\b/g) || []).forEach((pair) => {
    const words = pair.split(/\s+/).filter((w) => !/^[A-Z]\.$/.test(w));
    if (!words.every((w) => SAFE_PAIRS.test(w))) unsettled.push({ kind: 'possible name', what: pair });
  });
  (t.match(/\b\d{5,}\b/g) || []).forEach((n) => unsettled.push({ kind: 'long number', what: n }));
  const seen = new Set();
  return { text: t, unsettled: unsettled.filter((u) => { const k = u.kind + '|' + u.what; if (seen.has(k)) return false; seen.add(k); return true; }) };
}

// The key: code -> name. It travels with the ORIGINAL only (319 item 2).
export function codebookKey(book) {
  return [].concat(
    (book.people || []).map((p) => ({ code: p.code, name: p.name, role: p.role })),
    ...['places', 'trains', 'jobs', 'numbers'].map((k) => (book[k] || []).map((x) => ({ code: x.code, name: x.name, role: k }))));
}
